/**
 * reviewCardPrimitives.tsx — PLAT-10850 (DRY extraction)
 *
 * Shared styled-components for the Translation Review right-pane list panels
 * (`TranslationChangesPanel`, `TranslationMemoryPanel`). Extracted verbatim from
 * the Changes panel so both panels read with the same card balance and a restyle
 * lands in one place. Panel-specific chrome (inline editor, disclosure toggle,
 * term chips) stays in the owning panel.
 */

import styled from 'styled-components';

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Grey card matching the home story thumbnail's lower section (`var(--surface)`,
// 8px radius). Clickable when the entry resolves to a seekable source word —
// clicking jumps the transcript playhead there. `$highlighted` (the reviewer is
// hovering this entry's phrase in the transcript) lifts the card to the same
// elevated background as its own hover.
export const Card = styled.div<{ $clickable: boolean; $highlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background-color: ${props =>
    props.$highlighted ? 'var(--surface-elevated)' : 'var(--surface)'};
  border-radius: 8px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: background-color 150ms ease-out;

  ${props =>
    props.$clickable &&
    `&:hover { background-color: var(--surface-elevated); }`}
`;

// One stacked value row: a small muted label above the full-width text.
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// Compact label scale matching the edit popovers' primitives (11px label /
// 12px value) so the card reads with the same balance as the modal.
export const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
`;

export const FieldValue = styled.div`
  font-size: var(--text-xs);
  line-height: 1.4;
  word-break: break-word;
`;

// The source text is a quiet reference (translation-field pattern): flat muted
// text behind a 2px left rule, visibly not the payload of the card.
export const OriginalValue = styled(FieldValue)`
  color: var(--text-secondary);
  border-left: 2px solid var(--border-elevated);
  padding-left: 8px;
`;

// The current (edited / translated) value — the payload, brightest.
export const NowValue = styled(FieldValue)`
  color: var(--foreground);
`;

// One compact meta line. Parts are omitted when unknown so there are no
// dangling separators.
export const Meta = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

export const Empty = styled.div`
  font-size: var(--text-sm);
  color: var(--text-muted);
`;
